import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Tooltip,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import { SnackbarProvider } from 'notistack';
import { CustomTable, CustomSwitch, Toaster } from '@/components';
import {
  createOrUpdateProductServiceConfigService,
  deleteProductServiceConfigService,
} from '@/services';
import type { PaymentMethod } from '../PaymentMethodsScreen';
import { PaymentMethodsTableSkeleton } from './PaymentMethodsTableSkeleton';

const useStyles = makeStyles((theme) => ({
  actionsContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  methodsChips: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
  },
  chip: {
    height: 24,
    fontSize: '0.75rem',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
  },
}));

interface PaymentMethodsTableProps {
  paymentMethods: PaymentMethod[];
  setPaymentMethods: React.Dispatch<React.SetStateAction<PaymentMethod[]>>;
  enteredFilter: string;
  setPopUpAction: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      title: string;
      data: PaymentMethod | null;
    }>
  >;
  refreshData: () => void;
  actionsDisabled?: boolean;
  loading?: boolean;
}

export const PaymentMethodsTable: React.FC<PaymentMethodsTableProps> = ({
  paymentMethods,
  setPaymentMethods,
  enteredFilter,
  setPopUpAction,
  refreshData,
  actionsDisabled = false,
  loading = false,
}) => {
  const classes = useStyles();
  const providerRef = React.useRef<any>(null);
  const [filteredRows, setFilteredRows] = useState<PaymentMethod[]>([]);
  const [alert, setAlert] = useState({ show: false, type: 'success' as 'success' | 'error', text: '' });
  const [openAlertConfirm, setOpenAlertConfirm] = useState(false);
  const [openAlertConfirmDelete, setOpenAlertConfirmDelete] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<{
    id: string;
    name: string;
    checked: boolean;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  // Filtrar tabla según búsqueda
  useEffect(() => {
    if (!paymentMethods || !Array.isArray(paymentMethods)) {
      setFilteredRows([]);
      return;
    }

    const filtered = paymentMethods.filter(
      (method) =>
        method.product_name.toLowerCase().includes(enteredFilter.toLowerCase()) ||
        method.name.toLowerCase().includes(enteredFilter.toLowerCase()) ||
        method.description.toLowerCase().includes(enteredFilter.toLowerCase())
    );
    setFilteredRows(filtered);
  }, [enteredFilter, paymentMethods]);

  const handleChangeState = (event: React.ChangeEvent<HTMLInputElement>, methodId: string) => {
    const method = paymentMethods.find((m) => m.id === methodId);
    setSelectedMethod({
      id: methodId,
      name: method?.name || methodId,
      checked: event.target.checked,
    });
    setOpenAlertConfirm(true);
  };

  const handleDeleteMethod = (methodId: string) => {
    const method = paymentMethods.find((m) => m.id === methodId);
    setSelectedMethod({
      id: methodId,
      name: method?.name || methodId,
      checked: false,
    });
    setOpenAlertConfirmDelete(true);
  };

  const handleEditMethod = (methodId: string) => {
    const method = paymentMethods.find((m) => m.id === methodId);
    if (method) {
      setPopUpAction({
        open: true,
        title: 'Editar Método de Pago',
        data: method,
      });
    }
  };

  const confirmChangeState = async () => {
    if (!selectedMethod) return;
    setActionLoading(true);

    try {
      const methodToUpdate = paymentMethods.find((m) => m.id === selectedMethod.id);
      if (!methodToUpdate) return;

      // Transform to API structure
      const paymentMethodsSupported: string[] = [];
      if (methodToUpdate.paymentMethods.card) paymentMethodsSupported.push('CREDIT_CARD');
      if (methodToUpdate.paymentMethods.paymentButton) paymentMethodsSupported.push('BHD_BUTTON');
      if (methodToUpdate.paymentMethods.transfer) paymentMethodsSupported.push('BANK_TRANSFER');

      const features = (methodToUpdate.features || [])
        .map((f: any) => typeof f === 'string' ? f : f.value)
        .filter(Boolean);

      const productName = methodToUpdate.product_name || methodToUpdate.name;
      const productId = methodToUpdate.product_id || methodToUpdate.name;

      const payload = {
        product_name: productName,
        product_id: productId,
        config: {
          base: {
            type: [methodToUpdate.terminalType || 'ECOMMERCE'],
            acquisition: {
              payment_methods_supported: paymentMethodsSupported,
            },
            active: selectedMethod.checked,
            currency: 'DOP',
            description: methodToUpdate.description,
            fulfillment_type: methodToUpdate.terminalType === 'DISPOSITIVO' ? 'DELIVERY' : 'DIGITAL',
            labels: {
              not_requested: methodToUpdate.labels?.notRequested || '',
              features: features,
              requested: methodToUpdate.labels?.requested || '',
            },
            product_id: productId,
            requires_fulfillment: methodToUpdate.terminalType === 'DISPOSITIVO',
            status: selectedMethod.checked ? 'ACTIVE' : 'INACTIVE',
          },
          pricing: {
            amount: methodToUpdate.price,
            currency: 'DOP',
            model: [methodToUpdate.pricingModel || 'ONE_PAY'],
            product_id: productId,
            status: 'ACTIVE',
          },
        },
      };

      const res = await createOrUpdateProductServiceConfigService(payload);

      if (res?.status === 200) {
        providerRef.current?.enqueueSnackbar(
          `Método de pago ${selectedMethod.checked ? 'activado' : 'desactivado'} exitosamente`,
          {
            variant: 'success',
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
          }
        );
        refreshData();
      } else {
        setAlert({
          show: true,
          type: 'error',
          text: 'Ha ocurrido un error, intente nuevamente.',
        });
      }
    } catch (error) {
      console.error('Error updating payment method:', error);
      setAlert({
        show: true,
        type: 'error',
        text: 'Ha ocurrido un error, intente nuevamente.',
      });
    } finally {
      setActionLoading(false);
    }

    setOpenAlertConfirm(false);
    setSelectedMethod(null);
  };

  const confirmDelete = async () => {
    if (!selectedMethod) return;
    setActionLoading(true);

    try {
      const productId = selectedMethod.id;
      console.log('[Delete] Attempting to delete:', { configId: 'BASE', productId });
      const res = await deleteProductServiceConfigService('BASE', productId);
      console.log('[Delete] Response:', res);

      if (res?.status === 200 || res?.status === 204) {
        providerRef.current?.enqueueSnackbar('Método de pago eliminado exitosamente', {
          variant: 'success',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        });
        refreshData();
      } else {
        setAlert({
          show: true,
          type: 'error',
          text: 'Ha ocurrido un error, intente nuevamente.',
        });
      }
    } catch (error) {
      console.error('Error deleting payment method:', error);
      setAlert({
        show: true,
        type: 'error',
        text: 'Ha ocurrido un error al eliminar. Intente nuevamente.',
      });
    } finally {
      setActionLoading(false);
    }

    setOpenAlertConfirmDelete(false);
    setSelectedMethod(null);
  };

  const renderPaymentMethods = (methods: PaymentMethod['paymentMethods']) => {
    const activeMethodsLabels: string[] = [];
    if (methods.card) activeMethodsLabels.push('Tarjeta');
    if (methods.paymentButton) activeMethodsLabels.push('Botón de pago');
    if (methods.transfer) activeMethodsLabels.push('Transferencia');

    return (
      <Box className={classes.methodsChips}>
        {activeMethodsLabels.map((label) => (
          <Chip key={label} label={label} size="small" className={classes.chip} color="primary" />
        ))}
      </Box>
    );
  };

  const PRICING_MODEL_LABELS: Record<string, string> = {
    ONE_PAY: 'Pago Único',
    RENTAL: 'Alquiler',
    FREE: 'Gratis',
  };

  const TERMINAL_TYPE_LABELS: Record<string, string> = {
    DISPOSITIVO: 'Dispositivo',
    ECOMMERCE: 'eCommerce',
  };

  // Preparar filas con valores renderizados para CustomTable
  const preparedRows = filteredRows.map((method) => ({
    ...method,
    productNameDisplay: method.product_name || method.name || '-',
    terminalTypeDisplay: TERMINAL_TYPE_LABELS[method.terminalType] || method.terminalType || '-',
    priceDisplay: `$${method.price.toFixed(2)}`,
    pricingModelDisplay: PRICING_MODEL_LABELS[method.pricingModel] || method.pricingModel || '-',
    featuresDisplay: (method.features || [])
      .map((f: any) => typeof f === 'string' ? f : f.value)
      .filter(Boolean)
      .slice(0, 2)
      .join(', ') + ((method.features || []).length > 2 ? '...' : ''),
    paymentMethodsDisplay: renderPaymentMethods(method.paymentMethods),
    activeDisplay: (
      <CustomSwitch
        check={method.active}
        handleChange={(e: any) => handleChangeState(e, method.id)}
        checked={method.active}
        value={method.id}
        disabled={actionsDisabled}
        lblOff=""
        lblOn=""
      />
    ),
    actionsDisplay: (
      <Box className={classes.actionsContainer}>
        <Tooltip title="Editar">
          <IconButton
            size="small"
            onClick={() => handleEditMethod(method.id)}
            disabled={actionsDisabled}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar">
          <IconButton
            size="small"
            onClick={() => handleDeleteMethod(method.id)}
            disabled={actionsDisabled}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  }));

  const columns = [
    {
      id: 'productNameDisplay',
      label: 'Nombre',
      align: 'center' as const,
    },
    {
      id: 'terminalTypeDisplay',
      label: 'Tipo Terminal',
      align: 'center' as const,
    },
    {
      id: 'description',
      label: 'Descripción',
      align: 'center' as const,
    },
    {
      id: 'priceDisplay',
      label: 'Precio',
      align: 'center' as const,
    },
    {
      id: 'pricingModelDisplay',
      label: 'Modelo de Pago',
      align: 'center' as const,
    },
    {
      id: 'featuresDisplay',
      label: 'Características',
      align: 'center' as const,
    },
    {
      id: 'paymentMethodsDisplay',
      label: 'Métodos de pago',
      align: 'center' as const,
    },
    {
      id: 'activeDisplay',
      label: 'Estado',
      align: 'center' as const,
    },
    {
      id: 'actionsDisplay',
      label: 'Acciones',
      align: 'center' as const,
    },
  ];

  return (
    <>
      <SnackbarProvider ref={providerRef} maxSnack={3}>
        {loading ? (
          <PaymentMethodsTableSkeleton columns={columns} />
        ) : preparedRows.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="h6" gutterBottom>
              No hay medios de pago registrados
            </Typography>
            <Typography variant="body2">
              Pulse el bot\u00f3n "+" para agregar un nuevo medio de pago.
            </Typography>
          </Box>
        ) : (
          <CustomTable data={preparedRows} columns={columns} fullWidth />
        )}

        {/* Modal confirmación cambio de estado */}
        <Dialog
          open={openAlertConfirm}
          onClose={() => {
            setOpenAlertConfirm(false);
            setSelectedMethod(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Confirmar cambio de estado</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {`¿Está seguro que desea ${
                selectedMethod?.checked ? 'activar' : 'desactivar'
              } este método de pago?`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenAlertConfirm(false);
                setSelectedMethod(null);
              }}
              color="default"
              variant="outlined"
              disabled={actionLoading}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmChangeState}
              color="primary"
              variant="contained"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {actionLoading ? 'Procesando...' : 'Confirmar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal confirmación de eliminación */}
        <Dialog
          open={openAlertConfirmDelete}
          onClose={() => {
            setOpenAlertConfirmDelete(false);
            setSelectedMethod(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gridGap={8}>
              <WarningRoundedIcon style={{ color: '#E45501', fontSize: 28 }} />
              <Typography variant="h6" component="span" style={{ fontWeight: 600 }}>
                Eliminar medio de pago
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Está a punto de eliminar este método de pago. Esta acción es permanente y no podrá deshacerse.
              ¿Desea continuar?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenAlertConfirmDelete(false);
                setSelectedMethod(null);
              }}
              color="default"
              variant="outlined"
              disabled={actionLoading}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              style={{ backgroundColor: '#E45501', color: '#fff' }}
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={18} style={{ color: '#fff' }} /> : undefined}
            >
              {actionLoading ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Toaster
          show={alert.show}
          type={alert.type}
          text={alert.text}
          onClose={() => setAlert({ show: false, type: 'success', text: '' })}
        />
      </SnackbarProvider>
    </>
  );
};

export default PaymentMethodsTable;
